import { MOVIMENTACAO_BULK_ACTIONS_BY_STATUS, MOVIMENTACAO_STATUS_OPTIONS } from '../constants/movimentacoes.index.constants';
import {
    IMovimentacaoBulkActionOption,
    IMovimentacaoBulkActionTexts,
    IMovimentacaoResumoDisplay,
    IMovimentacaoSelectionContext,
    IMovimentacaoUiStatusOption,
    TMovimentacaoUiStatusKey,
    TMovimentacaoBulkAction
} from '../models/movimentacoes.index.model';
import {
    IMovimentacaoListItem,
    IMovimentacaoResumoAgrupador,
    IMovimentacaoResumoItem,
    IMovimentacaoResumoTotais,
    MOVIMENTACOES_LOTE_LIMIT
} from '../../models/movimentacoes.service.model';
import { isMovimentacoesLoteFatiado } from '../../helpers/movimentacoes-lote.helpers';

const MOVIMENTACAO_RESUMO_PLACEHOLDER_TOTALS: IMovimentacaoResumoTotais = {
    horas: '--',
    dias: '--',
    outros: '--'
};

const MOVIMENTACAO_RESUMO_ZERO_TOTALS: IMovimentacaoResumoTotais = {
    horas: '00:00',
    dias: '0',
    outros: '0.00'
};

/**
 * Resolve os textos de confirmação exibidos nas ações em lote da index.
 *
 * Sem `selectedCount`, devolve os textos do fluxo que aplica a ação a toda a aba.
 * Com uma quantidade selecionada, devolve a variante do modo de seleção.
 */
export function getMovimentacaoBulkActionConfirmTexts(
    action: TMovimentacaoBulkAction,
    selectedCount: number | null = null
): IMovimentacaoBulkActionTexts {
    if (typeof selectedCount === 'number' && selectedCount > 0) {
        return getMovimentacaoSelectionConfirmTexts(action, selectedCount);
    }

    if (action === 'approve') {
        return {
            title: 'Aprovar movimentações em lote',
            subtitle: 'Todas as movimentações elegíveis deste período na aba atual serão aprovadas.',
            confirmText: 'Tem certeza que deseja aprovar as movimentações em lote?',
            closeButton: 'Voltar',
            confirmButton: 'Aprovar'
        };
    }

    if (action === 'send') {
        return {
            title: 'Enviar movimentações em lote',
            subtitle: 'Todas as movimentações elegíveis deste período na aba atual serão enviadas.',
            confirmText: 'Tem certeza que deseja enviar as movimentações em lote?',
            closeButton: 'Voltar',
            confirmButton: 'Enviar'
        };
    }

    if (action === 'delete') {
        return {
            title: 'Excluir movimentações em lote',
            subtitle: 'Todas as movimentações elegíveis deste período na aba atual serão excluídas.',
            confirmText: 'Tem certeza que deseja excluir as movimentações em lote?',
            closeButton: 'Voltar',
            confirmButton: 'Excluir'
        };
    }

    return {
        title: 'Cancelar envio em lote',
        subtitle: 'Todas as movimentações elegíveis deste período na aba atual serão canceladas.',
        confirmText: 'Tem certeza que deseja cancelar as movimentações em lote?',
        closeButton: 'Voltar',
        confirmButton: 'Cancelar envio'
    };
}

/**
 * Formata a quantidade selecionada respeitando o singular de movimentação.
 */
export function formatMovimentacaoSelectionCount(count: number): string {
    const total = Number(count) || 0;

    return total === 1
        ? '1 movimentação'
        : `${total} movimentações`;
}

/**
 * Resolve o contador exibido na barra de ações do modo de seleção.
 */
export function getMovimentacaoSelectionCounterLabel(selecionados: number, selecionaveis: number): string {
    const total = Number(selecionados) || 0;

    if (!total) {
        return 'Nenhum item selecionado';
    }

    return `${total} de ${Number(selecionaveis) || 0} selecionados`;
}

/**
 * Resolve o aviso de escopo do modo de seleção quando a aba tem mais itens do que os carregados.
 */
export function getMovimentacaoSelectionScopeHint(carregados: number, totalDaAba: number | null | undefined): string {
    const loaded = Number(carregados) || 0;
    const total = Number(totalDaAba) || 0;

    if (!loaded || total <= loaded) {
        return '';
    }

    return `A seleção age apenas sobre as ${loaded} movimentações carregadas (a aba tem ${total}).`;
}

/**
 * Define se a movimentação pode participar de uma ação em lote específica.
 */
export function isMovimentacaoEligibleForBulkAction(
    entity: IMovimentacaoListItem,
    action: TMovimentacaoBulkAction,
    context: IMovimentacaoSelectionContext
): boolean {
    if (!entity) {
        return false;
    }

    const situacao = Number(entity.situacao);

    if (action === 'send' || action === 'delete') {
        return situacao === 0;
    }

    if (action === 'cancel') {
        // mesma fonte do drawer: a regra vem pronta do backend em `_podecancelar`
        return entity._podecancelar === true;
    }

    return situacao === 1;
}

/**
 * Define se a movimentação pode ser marcada na aba atual, considerando as ações disponíveis.
 */
export function isMovimentacaoSelectableForBulkActions(
    entity: IMovimentacaoListItem,
    actions: IMovimentacaoBulkActionOption[],
    context: IMovimentacaoSelectionContext
): boolean {
    return (actions || []).some((action) => isMovimentacaoEligibleForBulkAction(entity, action.value, context));
}

/**
 * Resolve o motivo exibido no tooltip das linhas que não podem ser marcadas.
 */
export function getMovimentacaoSelectionBlockedReason(
    entity: IMovimentacaoListItem,
    actions: IMovimentacaoBulkActionOption[],
    context: IMovimentacaoSelectionContext
): string {
    const isCancelOnly = (actions || []).length === 1 && actions[0].value === 'cancel';

    if (isCancelOnly && !isMovimentacaoSelectionCreator(entity, context)) {
        return 'Somente quem criou a movimentação pode cancelar o envio.';
    }

    return 'Esta movimentação não está elegível para as ações desta aba.';
}

/**
 * Resolve a mensagem de sucesso exibida após cada ação em lote.
 */
export function getMovimentacaoBulkActionSuccessMessage(action: TMovimentacaoBulkAction): string {
    if (action === 'approve') {
        return 'Movimentações aprovadas em lote com sucesso.';
    }

    if (action === 'send') {
        return 'Movimentações enviadas em lote com sucesso.';
    }

    if (action === 'delete') {
        return 'Movimentações excluídas em lote com sucesso.';
    }

    return 'Movimentações canceladas em lote com sucesso.';
}

/**
 * Resolve a mensagem padrão de erro para cada ação em lote.
 */
export function getMovimentacaoBulkActionErrorMessage(action: TMovimentacaoBulkAction): string {
    if (action === 'approve') {
        return 'Não foi possível aprovar as movimentações em lote.';
    }

    if (action === 'send') {
        return 'Não foi possível enviar as movimentações em lote.';
    }

    if (action === 'delete') {
        return 'Não foi possível excluir as movimentações em lote.';
    }

    return 'Não foi possível cancelar as movimentações em lote.';
}

/**
 * Resolve a lista de abas disponíveis na index.
 */
export function getVisibleMovimentacaoStatusOptions(workflowEnabled: boolean): IMovimentacaoUiStatusOption[] {
    return MOVIMENTACAO_STATUS_OPTIONS.filter((status) => {
        if (status.value === 'a_validar') {
            return workflowEnabled;
        }

        return true;
    });
}

/**
 * Resolve a aba selecionada após aplicar as regras de visibilidade da index.
 */
export function resolveVisibleMovimentacaoStatus(
    selectedStatus: IMovimentacaoUiStatusOption,
    visibleOptions: IMovimentacaoUiStatusOption[]
): IMovimentacaoUiStatusOption {
    const selectedStillVisible = visibleOptions.some((status) => status.value === selectedStatus.value);
    if (selectedStillVisible) {
        return selectedStatus;
    }

    return visibleOptions.find((status) => status.value === 'enviado')
        || visibleOptions[visibleOptions.length - 1]
        || MOVIMENTACAO_STATUS_OPTIONS[MOVIMENTACAO_STATUS_OPTIONS.length - 1];
}

/**
 * Resolve a primeira aba visível com itens para uso na carga inicial e troca de período.
 */
export function resolveInitialMovimentacaoStatusByTotals(
    visibleOptions: IMovimentacaoUiStatusOption[],
    fallbackStatus?: IMovimentacaoUiStatusOption
): IMovimentacaoUiStatusOption {
    const firstWithItems = (visibleOptions || []).find((status) =>
        typeof status.total === 'number' && status.total > 0
    );

    if (firstWithItems) {
        return firstWithItems;
    }

    return (visibleOptions || []).find((status) => status.value === 'todos')
        || fallbackStatus
        || visibleOptions[visibleOptions.length - 1]
        || MOVIMENTACAO_STATUS_OPTIONS[MOVIMENTACAO_STATUS_OPTIONS.length - 1];
}

/**
 * Resolve as ações em lote visíveis na index para a aba atual.
 */
export function getAvailableMovimentacaoBulkActions(
    statusValue: TMovimentacaoUiStatusKey,
    workflowEnabled: boolean
): IMovimentacaoBulkActionOption[] {
    const availableActions = MOVIMENTACAO_BULK_ACTIONS_BY_STATUS[statusValue] || [];

    return availableActions.filter((action) => action.value !== 'approve' || workflowEnabled);
}

/**
 * Aplica os totais das tabs da index a partir do resumo carregado do período.
 */
export function applyMovimentacaoStatusTotals(
    statusOptions: IMovimentacaoUiStatusOption[],
    resumoItems: IMovimentacaoResumoItem[]
): IMovimentacaoUiStatusOption[] {
    return (statusOptions || []).map((status) => ({
        ...status,
        total: resolveMovimentacaoResumoTotal(status.value, resumoItems)
    }));
}

/**
 * Cria o placeholder padrão exibido antes do carregamento do resumo do período.
 */
export function buildMovimentacaoResumoPlaceholder(): IMovimentacaoResumoDisplay {
    return {
        rendimentos: { ...MOVIMENTACAO_RESUMO_PLACEHOLDER_TOTALS },
        descontos: { ...MOVIMENTACAO_RESUMO_PLACEHOLDER_TOTALS }
    };
}

/**
 * Resolve os totalizadores exibidos na index a partir do resumo do período e da aba selecionada.
 */
export function resolveMovimentacaoResumoDisplay(
    statusValue: TMovimentacaoUiStatusKey,
    resumoItems: IMovimentacaoResumoItem[]
): IMovimentacaoResumoDisplay {
    const resumoItem = findMovimentacaoResumoItemForStatus(statusValue, resumoItems);

    if (!resumoItem) {
        return {
            rendimentos: { ...MOVIMENTACAO_RESUMO_ZERO_TOTALS },
            descontos: { ...MOVIMENTACAO_RESUMO_ZERO_TOTALS }
        };
    }

    return {
        rendimentos: normalizeResumoTotais(resumoItem.rendimentos),
        descontos: normalizeResumoTotais(resumoItem.descontos)
    };
}

/**
 * Resolve o total de movimentações exibido em cada tab da index a partir do resumo do período.
 */
export function resolveMovimentacaoResumoTotal(
    statusValue: TMovimentacaoUiStatusKey,
    resumoItems: IMovimentacaoResumoItem[]
): number {
    const resumoItem = findMovimentacaoResumoItemForStatus(statusValue, resumoItems);
    return resumoItem ? Number(resumoItem.movimentacoes) || 0 : 0;
}

function getMovimentacaoSelectionConfirmTexts(
    action: TMovimentacaoBulkAction,
    selectedCount: number
): IMovimentacaoBulkActionTexts {
    const quantidade = formatMovimentacaoSelectionCount(selectedCount);
    const fatiamentoAviso = isMovimentacoesLoteFatiado(selectedCount)
        ? ` O processamento será feito em lotes de até ${MOVIMENTACOES_LOTE_LIMIT} movimentações.`
        : '';
    const elegibilidadeAviso = ' Itens não elegíveis são ignorados.';

    if (action === 'approve') {
        return {
            title: 'Aprovar movimentações selecionadas',
            subtitle: `${quantidade} selecionada(s) será(ão) aprovada(s).${elegibilidadeAviso}${fatiamentoAviso}`,
            confirmText: 'Tem certeza que deseja aprovar as movimentações selecionadas?',
            closeButton: 'Voltar',
            confirmButton: 'Aprovar'
        };
    }

    if (action === 'send') {
        return {
            title: 'Enviar movimentações selecionadas',
            subtitle: `${quantidade} selecionada(s) será(ão) enviada(s). As demais permanecerão no status atual.${elegibilidadeAviso}${fatiamentoAviso}`,
            confirmText: 'Tem certeza que deseja enviar as movimentações selecionadas?',
            closeButton: 'Voltar',
            confirmButton: 'Enviar'
        };
    }

    if (action === 'delete') {
        return {
            title: 'Excluir movimentações selecionadas',
            subtitle: `${quantidade} selecionada(s) será(ão) excluída(s). Esta ação não pode ser desfeita.${elegibilidadeAviso}${fatiamentoAviso}`,
            confirmText: 'Tem certeza que deseja excluir as movimentações selecionadas?',
            closeButton: 'Voltar',
            confirmButton: 'Excluir'
        };
    }

    return {
        title: 'Cancelar movimentações selecionadas',
        subtitle: `${quantidade} selecionada(s) será(ão) cancelada(s).${elegibilidadeAviso}${fatiamentoAviso}`,
        confirmText: 'Tem certeza que deseja cancelar as movimentações selecionadas?',
        closeButton: 'Voltar',
        confirmButton: 'Cancelar envio'
    };
}

function isMovimentacaoSelectionCreator(
    entity: IMovimentacaoListItem,
    context: IMovimentacaoSelectionContext
): boolean {
    const createdBy = `${entity?.created_by?.email || ''}`.trim().toLowerCase();
    const currentUser = `${context?.currentUserEmail || ''}`.trim().toLowerCase();

    return !!createdBy && !!currentUser && createdBy === currentUser;
}

function findMovimentacaoResumoItemForStatus(
    statusValue: TMovimentacaoUiStatusKey,
    resumoItems: IMovimentacaoResumoItem[]
): IMovimentacaoResumoItem | null {
    if (!Array.isArray(resumoItems) || !resumoItems.length) {
        return null;
    }

    if (statusValue === 'todos') {
        return findMovimentacaoResumoItem(resumoItems, {});
    }

    if (statusValue === 'aguardando_estorno') {
        return findMovimentacaoResumoItem(resumoItems, {
            situacao: 1,
            wkf_estado: 'AGUARDANDO_ESTORNO'
        });
    }

    if (statusValue === 'rascunho') {
        return findMovimentacaoResumoItem(resumoItems, { situacao: 0 });
    }

    if (statusValue === 'enviado') {
        return findMovimentacaoResumoItem(resumoItems, { situacao: 1 });
    }

    if (statusValue === 'a_validar') {
        return findMovimentacaoResumoItem(resumoItems, { fase: 1 });
    }

    if (statusValue === 'efetuado') {
        return findMovimentacaoResumoItem(resumoItems, { situacao: 2 });
    }

    if (statusValue === 'cancelado') {
        return findMovimentacaoResumoItem(resumoItems, { situacao: 4 });
    }

    if (statusValue === 'recusado') {
        return findMovimentacaoResumoItem(resumoItems, { situacao: 5 });
    }

    if (statusValue === 'estornado') {
        return findMovimentacaoResumoItem(resumoItems, { situacao: 6 });
    }

    return null;
}

function findMovimentacaoResumoItem(
    resumoItems: IMovimentacaoResumoItem[],
    expectedAgrupador: IMovimentacaoResumoAgrupador
): IMovimentacaoResumoItem | null {
    return resumoItems.find((item) => isResumoAgrupadorMatch(item.agrupador, expectedAgrupador)) || null;
}

function isResumoAgrupadorMatch(
    agrupador: IMovimentacaoResumoAgrupador | [],
    expectedAgrupador: IMovimentacaoResumoAgrupador
): boolean {
    const normalizedAgrupador = normalizeResumoAgrupador(agrupador);
    const normalizedExpected = normalizeResumoAgrupador(expectedAgrupador);
    const keys: Array<keyof IMovimentacaoResumoAgrupador> = ['situacao', 'wkf_estado', 'fase'];

    return keys.every((key) => normalizedAgrupador[key] === normalizedExpected[key]);
}

function normalizeResumoAgrupador(
    agrupador: IMovimentacaoResumoAgrupador | []
): IMovimentacaoResumoAgrupador {
    if (!agrupador || Array.isArray(agrupador)) {
        return {};
    }

    return {
        situacao: typeof agrupador.situacao === 'number' ? agrupador.situacao : undefined,
        wkf_estado: typeof agrupador.wkf_estado === 'string' && agrupador.wkf_estado
            ? agrupador.wkf_estado
            : undefined,
        fase: typeof agrupador.fase === 'number' ? agrupador.fase : undefined
    };
}

function normalizeResumoTotais(totais?: Partial<IMovimentacaoResumoTotais> | null): IMovimentacaoResumoTotais {
    return {
        horas: normalizeResumoHoras(totais?.horas),
        dias: normalizeResumoDias(totais?.dias),
        outros: normalizeResumoCurrency(totais?.outros)
    };
}

function normalizeResumoHoras(value: unknown): string {
    if (typeof value !== 'string' || !value.trim()) {
        return MOVIMENTACAO_RESUMO_ZERO_TOTALS.horas;
    }

    const minutes = parseResumoHorasToMinutes(value);
    if (minutes === null) {
        return MOVIMENTACAO_RESUMO_ZERO_TOTALS.horas;
    }

    return formatResumoMinutes(minutes);
}

function normalizeResumoDias(value: unknown): string {
    const normalized = typeof value === 'number'
        ? value
        : Number(`${value ?? ''}`.replace(',', '.'));

    if (!Number.isFinite(normalized)) {
        return MOVIMENTACAO_RESUMO_ZERO_TOTALS.dias;
    }

    if (Number.isInteger(normalized)) {
        return normalized.toFixed(0);
    }

    return normalized.toFixed(1);
}

function normalizeResumoCurrency(value: unknown): string {
    const normalized = typeof value === 'number'
        ? value
        : Number(`${value ?? ''}`.replace(',', '.'));

    if (!Number.isFinite(normalized)) {
        return MOVIMENTACAO_RESUMO_ZERO_TOTALS.outros;
    }

    return normalized.toFixed(2);
}

function parseResumoHorasToMinutes(value: string): number | null {
    const normalizedValue = value.trim();
    const match = normalizedValue.match(/^(-?\d+):([0-5]\d)$/);
    if (!match) {
        return null;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);

    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
        return null;
    }

    const absoluteMinutes = (Math.abs(hours) * 60) + minutes;
    return hours < 0 ? -absoluteMinutes : absoluteMinutes;
}

function formatResumoMinutes(value: number): string {
    const sign = value < 0 ? '-' : '';
    const absoluteMinutes = Math.abs(value);
    const hours = Math.floor(absoluteMinutes / 60);
    const minutes = absoluteMinutes % 60;

    return `${sign}${hours < 10 ? `0${hours}` : `${hours}`}:${minutes < 10 ? `0${minutes}` : `${minutes}`}`;
}
